import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronDown, ChevronUp, RefreshCw, TrendingUp, TrendingDown, Award, Users, BookOpen, Sparkles } from 'lucide-react';

const CURRENCY_SYMBOL = {
  INR: '₹', USD: '$', CAD: 'C$', GBP: '£', AED: 'د.إ', EUR: '€', AUD: 'A$', SGD: 'S$',
};

const fmtMoney = (amount, currency = 'INR') => {
  const sym = CURRENCY_SYMBOL[currency] || '';
  const n = Number(amount || 0);
  return `${sym}${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const monthLabel = (m) => {
  if (!m || m.length < 7) return m;
  const [y, mo] = m.split('-');
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
};

function Row({ label, value, currency, negative = false, bold = false }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className={`text-slate-700 ${bold ? 'font-semibold' : ''}`}>{label}</span>
      <span
        className={`font-medium ${negative ? 'text-red-600' : 'text-slate-900'} ${bold ? 'font-bold' : ''}`}
      >
        {negative ? '-' : ''}{fmtMoney(Math.abs(value || 0), currency)}
      </span>
    </div>
  );
}

function Expandable({ title, count, total, currency, children, icon: Icon, color = 'blue' }) {
  const [open, setOpen] = useState(false);
  const colorMap = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    violet: 'bg-violet-50 text-violet-700 border-violet-100',
    red: 'bg-red-50 text-red-700 border-red-100',
  };
  return (
    <div className={`rounded-2xl border bg-white shadow-sm transition hover:shadow-md ${open ? 'ring-1 ring-slate-200' : ''}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full p-4 flex items-center justify-between text-left"
        data-testid={`earnings-expand-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-3">
          {Icon && (
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${colorMap[color]}`}>
              <Icon className="w-4 h-4" />
            </div>
          )}
          <div>
            <div className="font-semibold text-slate-900">{title}</div>
            {typeof count === 'number' && (
              <div className="text-xs text-slate-500">{count} {count === 1 ? 'item' : 'items'}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-900">{fmtMoney(total, currency)}</span>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-slate-100">{children}</div>}
    </div>
  );
}

export default function TeacherEarningsDashboard() {
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  const fetchMonths = useCallback(async () => {
    try {
      const res = await api.get('/teacher/earnings-v2/months');
      setMonths(res.data.months || []);
      if (!selectedMonth && res.data.months?.length > 0) {
        setSelectedMonth(res.data.months[0]);
      }
    } catch (e) {
      console.error(e);
    }
  }, [selectedMonth]);

  const fetchEarnings = useCallback(async (month) => {
    if (!month) return;
    setLoading(true);
    try {
      const res = await api.get(`/teacher/earnings-v2?month=${month}`);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load earnings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMonths(); }, [fetchMonths]);
  useEffect(() => { if (selectedMonth) fetchEarnings(selectedMonth); }, [selectedMonth, fetchEarnings]);

  const summaryMonths = useMemo(() => months.slice(0, 3), [months]);
  const [summaryTotals, setSummaryTotals] = useState({});

  useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      const totals = {};
      for (const m of summaryMonths) {
        try {
          const res = await api.get(`/teacher/earnings-v2?month=${m}`);
          if (cancelled) return;
          totals[m] = { total: res.data.grand_total, currency: res.data.display_currency };
        } catch {}
      }
      if (!cancelled) setSummaryTotals(totals);
    };
    if (summaryMonths.length > 0) loadAll();
    return () => { cancelled = true; };
  }, [summaryMonths]);

  if (!data && loading) {
    return <div className="p-6 text-slate-500">Loading earnings…</div>;
  }
  if (!data) {
    return (
      <div className="p-6">
        <div className="text-slate-500">No earnings data yet.</div>
      </div>
    );
  }

  const currency = data.display_currency || 'INR';
  const byCat = data.by_category || {};

  return (
    <div className="p-3 sm:p-6 max-w-5xl space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Earnings Dashboard</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Earnings reflect only after class completion <strong>and</strong> feedback submission.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-44" data-testid="earnings-month-select-v2"><SelectValue /></SelectTrigger>
            <SelectContent>
              {months.map(m => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Past 3 Months Summary cards */}
      {summaryMonths.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {summaryMonths.map((m) => (
            <button
              key={m}
              onClick={() => setSelectedMonth(m)}
              className={`text-left p-4 rounded-2xl bg-white border transition hover:shadow-md ${selectedMonth === m ? 'ring-2 ring-blue-400 border-blue-200' : 'border-slate-200'}`}
              data-testid={`earnings-month-card-${m}`}
            >
              <div className="text-xs text-slate-500 font-semibold">{monthLabel(m)}</div>
              <div className="mt-2 text-2xl font-bold text-emerald-600">
                {summaryTotals[m] ? fmtMoney(summaryTotals[m].total, summaryTotals[m].currency) : '—'}
              </div>
              <div className="mt-1 text-[11px] text-slate-400">Total payout</div>
            </button>
          ))}
        </div>
      )}

      {/* Total Earnings Hero */}
      <div className="rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-600 p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-emerald-100 text-sm">Total Earnings — {monthLabel(selectedMonth)}</p>
            <p className="text-4xl sm:text-5xl font-bold mt-1" data-testid="earnings-grand-total">
              {fmtMoney(data.grand_total, currency)}
            </p>
            <p className="text-emerald-100 text-xs mt-2">{data.classes_total} classes credited this month</p>
          </div>
          <Sparkles className="w-12 h-12 text-emerald-200 opacity-50 hidden sm:block" />
        </div>

        <div className="mt-5 pt-5 border-t border-white/20 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-emerald-100 text-xs">Paid Classes</div>
            <div className="font-bold text-lg">{fmtMoney(data.paid_classes_total, currency)}</div>
          </div>
          <div>
            <div className="text-emerald-100 text-xs">Group Classes</div>
            <div className="font-bold text-lg">{fmtMoney(data.group_classes_total, currency)}</div>
          </div>
          <div>
            <div className="text-emerald-100 text-xs">Incentives</div>
            <div className="font-bold text-lg">{fmtMoney(data.incentives_total + data.bonuses_total, currency)}</div>
          </div>
          <div>
            <div className="text-emerald-100 text-xs">Deductions</div>
            <div className="font-bold text-lg">−{fmtMoney(data.deductions_total, currency)}</div>
          </div>
        </div>
      </div>

      {/* Expandables */}
      <div className="space-y-3">
        {/* Paid Classes */}
        <Expandable
          title="Paid Classes"
          icon={BookOpen}
          color="blue"
          count={(byCat.paid_india?.count || 0) + (byCat.paid_intl?.count || 0)}
          total={data.paid_classes_total}
          currency={currency}
        >
          <div className="space-y-2 text-sm pt-2">
            <div className="flex justify-between">
              <span className="text-slate-700">India: <strong>{byCat.paid_india?.count || 0}</strong> × {fmtMoney(data.rate_card_local?.paid_india, currency)}</span>
              <span className="font-semibold text-slate-900">{fmtMoney(byCat.paid_india?.total_local, currency)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-700">USA/Canada: <strong>{byCat.paid_intl?.count || 0}</strong> × {fmtMoney(data.rate_card_local?.paid_intl, currency)}</span>
              <span className="font-semibold text-slate-900">{fmtMoney(byCat.paid_intl?.total_local, currency)}</span>
            </div>
            {(byCat.paid_india?.items || []).concat(byCat.paid_intl?.items || []).slice(0, 25).map((it, i) => (
              <div key={`paid-${i}`} className="flex justify-between text-xs text-slate-500 pl-3 border-l-2 border-slate-100">
                <span>{it.student_name || 'Student'} • {it.date}</span>
                <span>{fmtMoney(it.amount_local, currency)}</span>
              </div>
            ))}
          </div>
        </Expandable>

        {/* Group Classes */}
        <Expandable
          title="Group Classes"
          icon={Users}
          color="violet"
          count={(byCat.group_india?.count || 0) + (byCat.group_intl?.count || 0)}
          total={data.group_classes_total}
          currency={currency}
        >
          <div className="space-y-2 text-sm pt-2">
            <div className="flex justify-between">
              <span className="text-slate-700">India: <strong>{byCat.group_india?.count || 0}</strong> × {fmtMoney(data.rate_card_local?.group_india, currency)}</span>
              <span className="font-semibold text-slate-900">{fmtMoney(byCat.group_india?.total_local, currency)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-700">USA/Canada: <strong>{byCat.group_intl?.count || 0}</strong> × {fmtMoney(data.rate_card_local?.group_intl, currency)}</span>
              <span className="font-semibold text-slate-900">{fmtMoney(byCat.group_intl?.total_local, currency)}</span>
            </div>
          </div>
        </Expandable>

        {/* Incentives (auto + manual) */}
        <Expandable
          title="Incentives"
          icon={Award}
          color="emerald"
          count={(data.auto_incentives?.length || 0) + (data.manual_incentives?.length || 0)}
          total={data.incentives_total}
          currency={currency}
        >
          <div className="space-y-2 text-sm pt-2">
            {(data.auto_incentives || []).map((inc) => (
              <div key={inc.id} className="flex justify-between items-center text-slate-700" data-testid={`auto-incentive-${inc.id}`}>
                <span><Award className="w-3.5 h-3.5 inline text-emerald-500 mr-1" />{inc.label} <span className="text-xs text-slate-400">(auto · {inc.threshold} classes)</span></span>
                <span className="font-semibold text-emerald-700">{fmtMoney(inc.amount_local, currency)}</span>
              </div>
            ))}
            {(data.manual_incentives || []).map((inc) => (
              <div key={inc.id} className="flex justify-between items-center text-slate-700" data-testid={`manual-incentive-${inc.id}`}>
                <span>{inc.label} <span className="text-xs text-slate-400">(manual)</span></span>
                <span className="font-semibold text-emerald-700">{fmtMoney(inc.amount_local, currency)}</span>
              </div>
            ))}
            {(data.auto_incentives?.length || 0) + (data.manual_incentives?.length || 0) === 0 && (
              <div className="text-slate-400 text-xs">No incentives yet for this month.</div>
            )}
          </div>
        </Expandable>

        {/* Bonuses */}
        <Expandable
          title="Additional Bonus"
          icon={TrendingUp}
          color="amber"
          count={data.manual_bonuses?.length || 0}
          total={data.bonuses_total}
          currency={currency}
        >
          <div className="space-y-2 text-sm pt-2">
            {(data.manual_bonuses || []).map((b) => (
              <div key={b.id} className="flex justify-between text-slate-700">
                <span>{b.label}</span>
                <span className="font-semibold text-amber-700">{fmtMoney(b.amount_local, currency)}</span>
              </div>
            ))}
            {(data.manual_bonuses?.length || 0) === 0 && (
              <div className="text-slate-400 text-xs">No bonuses for this month.</div>
            )}
          </div>
        </Expandable>

        {/* Deductions */}
        <Expandable
          title="Deductions"
          icon={TrendingDown}
          color="red"
          count={data.deductions?.length || 0}
          total={data.deductions_total}
          currency={currency}
        >
          <div className="space-y-2 text-sm pt-2">
            {(data.deductions || []).map((d) => (
              <div key={d.id} className="flex justify-between text-red-600">
                <span>{d.label}</span>
                <span className="font-semibold">−{fmtMoney(d.amount_local, currency)}</span>
              </div>
            ))}
            {(data.deductions?.length || 0) === 0 && (
              <div className="text-slate-400 text-xs">No deductions for this month.</div>
            )}
          </div>
        </Expandable>
      </div>

      <p className="text-[11px] text-slate-400 text-center">
        Currency shown in <strong>{currency}</strong>. Rates auto-applied based on student region (India / International).
      </p>
    </div>
  );
}
